// Telegram deep links (SCREENS → Navigation). The same payloads arrive as the Mini App's
// `startapp` and as the bot's `/start` parameter, so web and bot parse them here.
// Bot notification buttons append `-n<code>` (T-1001) so a tap can be counted per type; the
// UUID is hex, so the `-n` can never be part of it. Telegram allows ≤64 chars of [A-Za-z0-9_-].
import type { NotificationType } from './notifications.js';

export type DeepLink =
	| { kind: 'event'; eventId: string }
	| { kind: 'chat'; eventId: string }
	| { kind: 'applications'; eventId: string }
	| { kind: 'user'; userId: string }
	| { kind: 'review'; eventId: string };

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const PATTERN = /^(event|chat|apps|user|review)_(.+)$/;
const NOTIFICATION_SUFFIX = /-n([a-z]{2})$/;

/** Short, stable codes (never reuse one): links stay short and survive reordering the types. */
const NOTIFICATION_CODES: Record<NotificationType, string> = {
	application_accepted: 'aa',
	application_rejected: 'ar',
	event_updated: 'eu',
	event_cancelled: 'ec',
	chat_message: 'cm',
	new_participant: 'np',
	new_application: 'na',
	event_reminder: 'er',
	review_request: 'rr',
	moderation_notice: 'mn',
};
const TYPE_BY_CODE = new Map(
	Object.entries(NOTIFICATION_CODES).map(([type, code]) => [code, type as NotificationType]),
);

/** The notification type a bot button link was made for, or null for any other link. */
export function notificationFromStartParam(
	startParam: string | null | undefined,
): NotificationType | null {
	if (startParam === null || startParam === undefined) return null;
	const code = NOTIFICATION_SUFFIX.exec(startParam)?.[1];
	return code === undefined ? null : (TYPE_BY_CODE.get(code) ?? null);
}

/** Returns null for anything unknown or malformed; never throws. */
export function parseStartParam(startParam: string | null | undefined): DeepLink | null {
	if (startParam === null || startParam === undefined) return null;
	const match = PATTERN.exec(startParam.replace(NOTIFICATION_SUFFIX, ''));
	const prefix = match?.[1];
	const id = match?.[2]?.toLowerCase();
	if (prefix === undefined || id === undefined || !UUID.test(id)) return null;

	switch (prefix) {
		case 'event':
			return { kind: 'event', eventId: id };
		case 'chat':
			return { kind: 'chat', eventId: id };
		case 'apps':
			return { kind: 'applications', eventId: id };
		case 'user':
			return { kind: 'user', userId: id };
		case 'review':
			return { kind: 'review', eventId: id };
		default:
			return null;
	}
}

/** The payload for a link (inverse of `parseStartParam`); `via`: the bot notification type. */
export function formatStartParam(link: DeepLink, via: NotificationType | null = null): string {
	const suffix = via === null ? '' : `-n${NOTIFICATION_CODES[via]}`;
	switch (link.kind) {
		case 'event':
			return `event_${link.eventId}${suffix}`;
		case 'chat':
			return `chat_${link.eventId}${suffix}`;
		case 'applications':
			return `apps_${link.eventId}${suffix}`;
		case 'user':
			return `user_${link.userId}${suffix}`;
		case 'review':
			return `review_${link.eventId}${suffix}`;
	}
}

const CAMPAIGN = /^[a-z0-9_-]{1,64}$/i;

/**
 * A campaign tag (`blogger_anna`, T-1005): any valid `startapp` value that is not a deep link.
 * Lower-cased; null for deep links and invalid values. Becomes the analytics `source`.
 */
export function campaignTag(startParam: string | null | undefined): string | null {
	if (startParam === null || startParam === undefined || !CAMPAIGN.test(startParam)) return null;
	return parseStartParam(startParam) === null ? startParam.toLowerCase() : null;
}

/** The Mini App link for a campaign: t.me/<bot>/<app>?startapp=<tag>. Throws on a bad tag. */
export function campaignLink(bot: string, app: string, tag: string): string {
	const normalized = campaignTag(tag);
	if (normalized === null)
		throw new Error('a campaign tag is 1–64 of a-z, 0-9, _ and -, not a deep link');
	return `https://t.me/${bot}/${app}?startapp=${normalized}`;
}

/** Opens the Mini App (on `link` if given): t.me/<bot>/<app>[?startapp=…]. */
export function miniAppLink(
	bot: string,
	app: string,
	link: DeepLink | null = null,
	via: NotificationType | null = null,
): string {
	const base = `https://t.me/${bot}/${app}`;
	return link === null ? base : `${base}?startapp=${formatStartParam(link, via)}`;
}
